import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, Loading, inputCls, labelCls, btnPrimary } from "@/components/kit";
import { Brain, Smiley, SmileyMeh, Warning, Plus, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";

const sentiments = [
  { v: "positive", label: "Positive", color: "#34C759", icon: Smiley },
  { v: "neutral", label: "Neutral", color: "#A3A3A3", icon: SmileyMeh },
  { v: "concern", label: "Concern", color: "#FF3B30", icon: Warning },
];
const categories = ["Participation", "Respect", "Focus", "Teamwork", "Disruption"];
const sMeta = Object.fromEntries(sentiments.map((s) => [s.v, s]));

export default function Behavior() {
  const { user } = useAuth();
  const canLog = ["teacher", "athletic_director"].includes(user?.role);
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(null);
  const [trends, setTrends] = useState(null);
  const [form, setForm] = useState({ sentiment: "positive", category: "Participation", note: "" });

  useEffect(() => {
    api.get("/users", { params: { role: "athlete" } }).then((r) => { setStudents(r.data); if (r.data[0]) setSelected(r.data[0].id); });
  }, []);

  const load = async (sid) => { setTrends(null); const r = await api.get("/behavior/trends", { params: { student_id: sid } }); setTrends(r.data); };
  useEffect(() => { if (selected) load(selected); }, [selected]);

  const save = async () => {
    try { await api.post("/behavior", { student_id: selected, sentiment: form.sentiment, category: form.category, note: form.note || null }); toast.success("Behavior logged"); setForm({ ...form, note: "" }); load(selected); }
    catch (e) { toast.error("Could not save"); }
  };

  if (!students) return <Loading />;
  const sel = students.find((s) => s.id === selected);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow="Classroom Behavior" title="Behavior" accent="trends" icon={Brain} />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 items-start">
        <Card className="p-3 lg:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold px-2 py-2">Students</div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {students.map((s) => (
              <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`behavior-student-${s.id}`}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left transition-colors ${selected === s.id ? "bg-[#FF3B30]/15" : "hover:bg-white/5"}`}>
                <Avatar name={s.name} size={32} color="#34C759" />
                <div className="min-w-0"><div className="text-sm text-white truncate">{s.name}</div><div className="text-[11px] text-[#737373]">{s.sport}</div></div>
              </button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-3">
          {!trends ? <Loading /> : (
            <div className="space-y-6">
              {sel && <div className="flex items-center gap-3"><Avatar name={sel.name} size={44} color="#34C759" /><div><div className="font-head text-2xl font-bold uppercase tracking-tight">{sel.name}</div><div className="text-xs text-[#737373]">{trends.total} behavior notes</div></div></div>}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Behavior Score</div><div className="font-mono text-3xl font-black" style={{ color: trends.score >= 50 ? "#34C759" : trends.score >= 0 ? "#FF9F0A" : "#FF3B30" }} data-testid="behavior-score">{trends.score > 0 ? "+" : ""}{trends.score}</div></Card>
                {sentiments.map((s) => (
                  <Card key={s.v} className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">{s.label}</div><div className="font-mono text-3xl font-black" style={{ color: s.color }} data-testid={`behavior-count-${s.v}`}>{trends.sentiments[s.v]}</div></Card>
                ))}
              </div>

              <Card className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4">By Category</div>
                <div className="space-y-3">
                  {trends.by_category.map((c) => {
                    const tot = c.positive + c.neutral + c.concern;
                    return (
                      <div key={c.category}>
                        <div className="flex justify-between text-sm mb-1"><span className="text-white">{c.category}</span><span className="font-mono text-[#737373]">{tot}</span></div>
                        <div className="h-2.5 bg-white/5 rounded-sm overflow-hidden flex">
                          <div style={{ width: `${(c.positive / tot) * 100}%`, background: "#34C759" }} />
                          <div style={{ width: `${(c.neutral / tot) * 100}%`, background: "#A3A3A3" }} />
                          <div style={{ width: `${(c.concern / tot) * 100}%`, background: "#FF3B30" }} />
                        </div>
                      </div>
                    );
                  })}
                  {trends.by_category.length === 0 && <div className="text-sm text-[#737373]">No behavior logged yet.</div>}
                </div>
              </Card>

              {canLog && (
                <Card className="p-6" data-testid="behavior-log-form">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4"><NotePencil size={16} weight="fill" color="#FF3B30" /> Log Behavior</div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {sentiments.map((s) => (
                      <button key={s.v} onClick={() => setForm({ ...form, sentiment: s.v })} data-testid={`sentiment-${s.v}`}
                        className={`flex flex-col items-center gap-1 py-3 rounded-sm transition-colors ${form.sentiment === s.v ? "text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}
                        style={form.sentiment === s.v ? { background: s.color } : {}}>
                        <s.icon size={22} weight="fill" /> <span className="text-[10px] uppercase tracking-wider font-semibold">{s.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="behavior-category-select">{categories.map((c) => <option key={c}>{c}</option>)}</select>
                    <input className={`${inputCls} md:col-span-2`} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" data-testid="behavior-note-input" />
                  </div>
                  <button onClick={save} className={`${btnPrimary} w-full mt-3`} data-testid="save-behavior-button"><Plus size={16} weight="bold" /> Log Behavior</button>
                </Card>
              )}

              <Card className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4">Recent Notes</div>
                <div className="space-y-2" data-testid="behavior-recent">
                  {trends.recent.map((b) => {
                    const m = sMeta[b.sentiment];
                    return (
                      <div key={b.id} className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
                        <m.icon size={20} weight="fill" color={m.color} className="mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white"><span className="font-semibold">{b.category}</span>{b.note ? ` — ${b.note}` : ""}</div>
                          <div className="text-[11px] text-[#737373]">{b.date} · {b.teacher_name}</div>
                        </div>
                      </div>
                    );
                  })}
                  {trends.recent.length === 0 && <div className="text-sm text-[#737373]">No notes yet.</div>}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
